import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Edit2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SignaturesList({ basePath = "/account/signatures" }) {
  const navigate = useNavigate();
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      const res = await axios.get(`${API}/signatures`);
      setSignatures(res.data || []);
    } catch (err) {
      toast.error("Could not load your signatures");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSignature = async (id) => {
    if (!window.confirm("Are you sure you want to delete this signature?")) return;
    try {
      await axios.delete(`${API}/signatures/${id}`);
      setSignatures(prev => prev.filter(s => s.id !== id));
      toast.success("Signature deleted");
    } catch (err) {
      toast.error("Failed to delete signature");
    }
  };

  const copyToClipboard = async (html) => {
    try {
      const blob = new Blob([html], { type: "text/html" });
      const data = [new ClipboardItem({ "text/html": blob })];
      await navigator.clipboard.write(data);
      toast.success("Signature copied to clipboard! You can now paste it into your email client.");
    } catch (err) {
      toast.error("Copy failed. Open the signature and use Copy HTML Code instead.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-black" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-xl font-semibold">Email Signatures</h2>
          <p className="text-slate-500 mt-1">Build and manage your email signatures.</p>
        </div>
        <button
          onClick={() => navigate(`${basePath}/new`)}
          className="bg-black text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 hover:scale-[1.02] transition-all"
        >
          <Plus className="w-5 h-5" />
          New Signature
        </button>
      </div>

      {signatures.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-[40px] p-24 text-center">
          <div className="bg-gray-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Plus className="w-10 h-10 text-gray-300" />
          </div>
          <h2 className="text-2xl font-bold mb-2">No signatures yet</h2>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">Create your first professional email signature in minutes using our visual builder.</p>
          <button onClick={() => navigate(`${basePath}/new`)} className="text-black font-bold border-b-2 border-black">Get Started Now</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {signatures.map((sig) => (
            <div
              key={sig.id}
              className="bg-white rounded-[32px] overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all group"
            >
              <div className="p-6 h-48 bg-gray-50 flex items-center justify-center overflow-hidden">
                <div
                  className="scale-[0.6] origin-center bg-white p-4 rounded shadow-sm"
                  dangerouslySetInnerHTML={{ __html: sig.html_content }}
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold mb-1 truncate">
                  {sig.name === "New Signature" ? (sig.fields?.email || sig.name) : sig.name}
                </h3>
                {sig.name !== "New Signature" && sig.fields?.email && (
                  <p className="text-sm text-gray-500 mb-1 truncate">{sig.fields.email}</p>
                )}
                <p className="text-xs text-gray-400 mb-6 uppercase tracking-widest font-bold">
                  Last updated {new Date(sig.updated_at).toLocaleDateString()}
                </p>

                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => copyToClipboard(sig.html_content)}
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-gray-50 hover:bg-black hover:text-white transition-all text-gray-600"
                    title="Copy to Clipboard"
                  >
                    <Copy className="w-5 h-5" />
                    <span className="text-[10px] font-bold">Copy</span>
                  </button>
                  <button
                    onClick={() => navigate(`${basePath}/edit/${sig.id}`)}
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-gray-50 hover:bg-black hover:text-white transition-all text-gray-600"
                  >
                    <Edit2 className="w-5 h-5" />
                    <span className="text-[10px] font-bold">Edit</span>
                  </button>
                  <button
                    onClick={() => deleteSignature(sig.id)}
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-gray-50 hover:bg-red-500 hover:text-white transition-all text-gray-600"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="text-[10px] font-bold">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
